// ─────────────────────────────────────────────────────────────
// utils/formato.js — Helpers de formato compartidos.
// Una sola fuente de verdad para fechas, moneda, edad, etc.
// Cualquier vista que necesite uno de estos formatos debe
// importar desde aquí en vez de duplicar la función localmente.
// ─────────────────────────────────────────────────────────────

// ── Moneda ───────────────────────────────────────────────────
// Formato: "1.234,56 €" (español, dos decimales).
export const fmtEur = (n) =>
  `${Number(n ?? 0).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

// ── Fechas ───────────────────────────────────────────────────
// Convención: aceptan string ISO ("YYYY-MM-DD") y devuelven
// "—" (o "" en variantes del portal) si el input es falsy.

// "04 may 2026" — uso general en tablas y listas.
export const fmtFechaCorta = (f, vacio = '—') => {
  if (!f) return vacio;
  return new Date(f + 'T00:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

// "4 de mayo" — sin año, para contextos donde el año está implícito (cobros del mes).
export const fmtFechaSinAnio = (f, vacio = '—') => {
  if (!f) return vacio;
  return new Date(f + 'T00:00:00').toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long',
  });
};

// "Lunes, 4 de mayo" — primera letra mayúscula.
export const fmtFechaLarga = (f, vacio = '—') => {
  if (!f) return vacio;
  const d = new Date(f + 'T12:00:00');
  const s = d.toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// "04 may 2026 14:30" — para timestamps con hora.
export const fmtFechaHora = (ts, vacio = '—') => {
  if (!ts) return vacio;
  return new Date(ts).toLocaleString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// "14:30" — extrae HH:MM de un string "HH:MM:SS".
export const fmtHora = (h) => h ? h.slice(0, 5) : '—';

// "2026-05-04" — para inputs <input type="date"> y queries al backend.
export const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ── Edad ─────────────────────────────────────────────────────
// Devuelve número de años cumplidos, o null si no hay fecha.
export const calcEdad = (fechaNac) => {
  if (!fechaNac) return null;
  const hoy = new Date();
  const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad;
};

// ── Fecha relativa ───────────────────────────────────────────
// "Hoy" / "Ayer" / "Hace 6 días" / "Hace 2 sem." / "Hace 3 meses" / "Hace 1 año".
export const fechaRelativa = (fecha) => {
  if (!fecha) return null;
  const hoy = new Date();
  const f = new Date(fecha + 'T12:00:00');
  const diffMs = hoy - f;
  const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Ayer';
  if (dias < 7) return `Hace ${dias} días`;
  if (dias < 30) return `Hace ${Math.floor(dias / 7)} sem.`;
  if (dias < 365) return `Hace ${Math.floor(dias / 30)} mes${Math.floor(dias / 30) > 1 ? 'es' : ''}`;
  return `Hace ${Math.floor(dias / 365)} año${Math.floor(dias / 365) > 1 ? 's' : ''}`;
};

// ── Avatar / iniciales ───────────────────────────────────────
const AVATAR_COLORS = [
  '#006a62', '#041627', '#8B5CF6', '#3B82F6', '#EC4899',
  '#F59E0B', '#10B981', '#6366F1', '#14B8A6', '#F97316',
];

// Color consistente para el avatar en función del nombre.
export const getAvatarColor = (nombre) => {
  if (!nombre) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < nombre.length; i++) hash = nombre.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Iniciales 2 letras: primera de nombre + primera de apellidos.
export const getIniciales = (nombre, apellidos) => {
  const n = (nombre || '')[0] || '';
  const a = (apellidos || '')[0] || '';
  return (n + a).toUpperCase() || '?';
};
